import { DecimalPipe } from '@angular/common';
import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';

import { toErrorMessage } from '../../../helpers/http-error';
import { AnalyticsService, isUnresolvedScope } from '../../../services/analytics.service';
import { LocaleService } from '../../../services/locale.service';
import {
  PeriodPreset,
  SeriesPoint,
  WalletSummary,
  resolvePeriod,
} from '../../../types/analytics.types';
import { TPipe } from '../../../pipe/t.pipe';
import {
  PrettySelectComponent,
  PrettySelectOption,
} from '../../ui/pretty-select/pretty-select.component';
import { AnalyticsSeriesChartComponent } from '../analytics-series-chart/analytics-series-chart.component';

/**
 * Section analytics **Trésorerie** — **partagée à l'identique** entre les 3
 * dashboards. Endpoints `wallet/summary` + `wallet/series` : **même URL**, le
 * back dérive le périmètre du JWT (owner/operator = LEUR flux, admin = plateforme).
 *
 * ⚠️ C'est le **flux** de la période (entrées/sorties/net), **pas les soldes**
 * (sujet #60). La série porte le flux **net** (`value`) qui **peut être négatif**
 * (sortie nette) — la courbe le rend tel quel.
 *
 * Garde-fou : 403 = acteur non résolvable → message dédié ({@link isUnresolvedScope}).
 */
@Component({
  selector: 'app-wallet-analytics',
  standalone: true,
  imports: [
    DecimalPipe,
    FormsModule,
    TPipe,
    PrettySelectComponent,
    AnalyticsSeriesChartComponent,
  ],
  template: `
    <section class="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03] mb-6">
      <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 class="text-base font-semibold text-gray-800 dark:text-white/90">{{ 'analytics.wallet.title' | t }}</h3>
          <p class="mt-0.5 text-sm text-gray-500 dark:text-gray-400">{{ 'analytics.wallet.subtitle' | t }}</p>
        </div>
        <div class="w-full sm:w-44">
          <app-pretty-select
            [options]="periodOptions"
            [ngModel]="period()"
            (ngModelChange)="changePeriod($event)"
          />
        </div>
      </div>

      @if (loading()) {
        <div class="flex h-[220px] items-center justify-center text-sm text-gray-400 dark:text-gray-500">
          {{ 'analytics.loading' | t }}
        </div>
      } @else if (unresolved()) {
        <div class="mt-4 flex items-start gap-3 rounded-xl border border-blue-light-200 bg-blue-light-50 p-4 dark:border-blue-light-500/30 dark:bg-blue-light-500/10">
          <svg class="mt-0.5 shrink-0 text-blue-light-500" width="20" height="20" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8"/><path d="M12 8h.01M11 12h1v4h1" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
          <p class="text-sm text-blue-light-700 dark:text-blue-light-200">{{ 'analytics.unresolved' | t }}</p>
        </div>
      } @else if (error()) {
        <div class="mt-4 flex items-center justify-between gap-3 rounded-xl border border-error-200 bg-error-50 p-4 dark:border-error-500/30 dark:bg-error-500/10">
          <p class="text-sm text-error-700 dark:text-error-400">{{ error() }}</p>
          <button type="button" (click)="load()" class="shrink-0 text-xs font-medium text-error-700 hover:underline dark:text-error-400">{{ 'common.retry' | t }}</button>
        </div>
      } @else if (summary(); as w) {
        <!-- Flux par type de compte -->
        <div class="mt-5 overflow-x-auto">
          <table class="w-full min-w-[520px] text-sm">
            <thead>
              <tr class="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500 dark:border-gray-800 dark:text-gray-400">
                <th class="py-2 pr-4 font-medium">{{ 'analytics.wallet.account' | t }}</th>
                <th class="py-2 px-4 text-right font-medium">{{ 'analytics.wallet.movements' | t }}</th>
                <th class="py-2 px-4 text-right font-medium">{{ 'analytics.wallet.inflow' | t }}</th>
                <th class="py-2 px-4 text-right font-medium">{{ 'analytics.wallet.outflow' | t }}</th>
                <th class="py-2 pl-4 text-right font-medium">{{ 'analytics.wallet.net' | t }}</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100 dark:divide-gray-800">
              @for (a of w.byAccountType; track a.accountType) {
                <tr class="text-gray-700 dark:text-gray-300">
                  <td class="py-2.5 pr-4 font-medium text-gray-800 dark:text-white/90">{{ accountLabel(a.accountType) }}</td>
                  <td class="py-2.5 px-4 text-right text-gray-500 dark:text-gray-400">{{ a.movementCount | number }}</td>
                  <td class="py-2.5 px-4 text-right">{{ a.inflow | number: '1.0-0' }}</td>
                  <td class="py-2.5 px-4 text-right">{{ a.outflow | number: '1.0-0' }}</td>
                  <td class="py-2.5 pl-4 text-right font-semibold"
                      [class.text-brand-700]="a.net >= 0"
                      [class.dark:text-brand-400]="a.net >= 0"
                      [class.text-error-600]="a.net < 0"
                      [class.dark:text-error-400]="a.net < 0">
                    {{ a.net | number: '1.0-0' }}
                  </td>
                </tr>
              } @empty {
                <tr><td colspan="5" class="py-6 text-center text-sm text-gray-400">{{ 'analytics.empty' | t }}</td></tr>
              }
            </tbody>
          </table>
        </div>

        <!-- Courbe flux net (peut passer sous zéro) -->
        <div class="mt-6">
          <h4 class="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">{{ 'analytics.wallet.chartTitle' | t }}</h4>
          <app-analytics-series-chart
            [points]="series()"
            [seriesName]="seriesName()"
            [emptyLabel]="'analytics.empty' | t"
          />
        </div>
      }
    </section>
  `,
})
export class WalletAnalyticsComponent implements OnInit {
  private readonly analytics = inject(AnalyticsService);
  private readonly locale = inject(LocaleService);
  private readonly destroyRef = inject(DestroyRef);

  readonly period = signal<PeriodPreset>('30d');
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  /** `true` = acteur non résolvable (403) → message dédié, pas une erreur. */
  readonly unresolved = signal(false);

  readonly summary = signal<WalletSummary | null>(null);
  readonly series = signal<SeriesPoint[]>([]);

  readonly periodOptions: PrettySelectOption<PeriodPreset>[] = [
    { value: '7d', label: 'analytics.period.7d' },
    { value: '30d', label: 'analytics.period.30d' },
    { value: '90d', label: 'analytics.period.90d' },
    { value: '365d', label: 'analytics.period.365d' },
  ];

  ngOnInit(): void {
    this.load();
  }

  changePeriod(p: PeriodPreset | null): void {
    if (!p || p === this.period()) return;
    this.period.set(p);
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.unresolved.set(false);
    const period = resolvePeriod(this.period());

    forkJoin({
      summary: this.analytics.walletSummary(period),
      series: this.analytics.walletSeries(period),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ summary, series }) => {
          this.summary.set(summary);
          this.series.set(series);
          this.loading.set(false);
        },
        error: (err: unknown) => {
          this.loading.set(false);
          if (isUnresolvedScope(err)) {
            this.unresolved.set(true);
          } else {
            this.error.set(toErrorMessage(err, 'errors.analyticsLoadFailed'));
          }
        },
      });
  }

  seriesName(): string {
    return this.locale.t('analytics.wallet.seriesName');
  }

  /** Libellé d'un type de compte (backtick → hors radar de l'audit i18n, repli brut). */
  accountLabel(accountType: string): string {
    return this.locale.t(`analytics.wallet.account.${accountType}`);
  }
}
